import { useUser } from '@/hooks/use-user';
import { purgeCsvDealStorage } from '@/lib/csv-offer/storage';
import { ReopenDealNegotiationDocument } from '@/lib/graphql/generated/graphql';
import { isAbortError } from '@/lib/utils/is-abort-error';
import { dealAtom, dealRefetchAtom } from '@/states/deal.state';
import { useMutation } from '@apollo/client/react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

export function useReopenNegotiation(dealId: string) {
  const dealRefetch = useAtomValue(dealRefetchAtom);
  const setDeal = useSetAtom(dealAtom);
  const store = useStore();
  const userId = useUser()?.providerUser.sub ?? null;
  const [reopenDealNegotiationMutation, { loading, error }] = useMutation(
    ReopenDealNegotiationDocument
  );

  const reopenNegotiation = async () => {
    const wasOfferControlFlow =
      store.get(dealAtom)?.isOfferControlFlow ?? false;
    const result = await reopenDealNegotiationMutation({
      variables: { dealId },
    });

    const response = result.data?.reopenDealNegotiation;
    if (response?.__typename === 'DomainException') {
      throw new Error(response.code);
    }

    if (wasOfferControlFlow) {
      setDeal((prev) => (prev ? { ...prev, isOfferControlFlow: false } : null));
      if (userId) purgeCsvDealStorage(userId, dealId);
    }

    // The mutation already succeeded. A concurrent subscription refresh can
    // supersede this request, so the UI refresh must remain best effort.
    try {
      await dealRefetch.fn();
    } catch (refetchError) {
      if (!isAbortError(refetchError)) {
        console.error(
          'Failed to refetch deal after reopening negotiation:',
          refetchError
        );
      }
    }
    return result;
  };

  return {
    reopenNegotiation,
    loading,
    error,
  };
}
